/**
 * CanonicalSavePipeline — orchestrates the optional Phase 12 / 13
 * passes at recording-Stop time and emits a v3.2 CanonicalRecording
 * ready for serialisation via encodeCanonicalRecording().
 *
 * The host calls `buildCanonicalSavePayload({...})` once after the
 * source has stopped emitting frames. The pipeline:
 *   1. Optionally runs the 2D→3D PoseLifter (Phase 12) over the
 *      full CanonicalFrame[] sequence.
 *   2. Optionally runs the depth + scale estimator (Phase 13) on a
 *      single representative frame and image.
 *   3. Bundles the results into a CanonicalRecording.
 *
 * Both passes are best-effort: failures are logged and produce null
 * fields so the saved recording is still loadable by older readers.
 *
 * docs/migration-plan.md Phase 12, 13.
 */

import { liftPoseSequence, type Lifted3DSeries, type PoseLifterAdapter } from '../core/PoseLifter';
import {
  estimateSubjectScale,
  type DepthMapView,
  type SceneCalibration,
  type SubjectScaleOptions,
} from '../core/SubjectScaleEstimator';
import type { CanonicalFrame } from '../canonical/CanonicalFrame';
import type { StreamHeader } from '../canonical/StreamHeader';
import type { SubjectProfile } from '../canonical/SubjectProfile';
import {
  makeCanonicalRecording,
  type CanonicalRecording,
} from './CanonicalPersistenceFormat';

export interface DepthEstimatorLike {
  initialize(): Promise<void>;
  estimate(source: CanvasImageSource): Promise<DepthMapView | null>;
  dispose(): Promise<void>;
}

export interface SavePipelineOptions {
  header: StreamHeader;
  frames: CanonicalFrame[];
  subject?: SubjectProfile | null;
  sessionId?: string | null;

  /** Phase 12. Pass null / undefined to skip the lifter pass. */
  lifter?: PoseLifterAdapter | null;
  /** Stride between successive lifter windows. Defaults to half the
   *  adapter's window length. */
  lifterWindowStride?: number;

  /** Phase 13. When provided, the depth pass runs on
   *  `representativeFrame` (typically the source video frame at
   *  scene-midpoint or a detected upright posture). */
  depth?: DepthEstimatorLike | null;
  /** Image source for depth estimation. Required when `depth` is set. */
  representativeImage?: CanvasImageSource | null;
  /** CanonicalFrame paired with `representativeImage`. Required when
   *  depth is set. The estimator samples landmarks at this frame. */
  representativeFrame?: CanonicalFrame | null;
  /** Forwarded to estimateSubjectScale (height anchor, foot indices). */
  scaleOptions?: SubjectScaleOptions;
}

export interface SavePipelineDiagnostics {
  lifterRan: boolean;
  depthRan: boolean;
  errors: string[];
}

export interface SavePipelineResult {
  recording: CanonicalRecording;
  diagnostics: SavePipelineDiagnostics;
}

export async function buildCanonicalSavePayload(
  opts: SavePipelineOptions,
): Promise<SavePipelineResult> {
  const errors: string[] = [];
  let lifted: Lifted3DSeries | null = null;
  let scene: SceneCalibration | null = null;

  if (opts.lifter && opts.frames.length >= opts.lifter.windowFrames) {
    try {
      lifted = await liftPoseSequence(opts.frames, {
        adapter: opts.lifter,
        windowStride: opts.lifterWindowStride,
      });
    } catch (err) {
      errors.push(`lifter: ${err instanceof Error ? err.message : String(err)}`);
      lifted = null;
    }
  }

  if (opts.depth && opts.representativeImage && opts.representativeFrame) {
    try {
      await opts.depth.initialize();
      const depthMap = await opts.depth.estimate(opts.representativeImage);
      if (depthMap) {
        scene = estimateSubjectScale(
          opts.representativeFrame,
          depthMap,
          opts.scaleOptions,
        );
      }
    } catch (err) {
      errors.push(`depth: ${err instanceof Error ? err.message : String(err)}`);
      scene = null;
    } finally {
      try {
        await opts.depth.dispose();
      } catch {
        /* ignore */
      }
    }
  }

  const recording = makeCanonicalRecording(
    opts.header,
    opts.frames,
    opts.subject ?? null,
    opts.sessionId ?? null,
    lifted,
    scene,
  );

  return {
    recording,
    diagnostics: {
      lifterRan: lifted !== null,
      depthRan: scene !== null,
      errors,
    },
  };
}
